import { runningStatsPresentation, recordedNumber } from '../../utils/runningStatsPresentation';
import { isRunningWorkout } from '../../utils/runningWorkoutMetrics';
import React, { useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import type { WorkoutDetail } from '../../services/api';
import { WorkoutCurveCard as PdcCurveChart } from '../../components/charts/WorkoutCurveCard';
import { useChartLandscapeFullscreen, useFullscreenChartHeight } from '../../hooks/useChartLandscapeFullscreen';
import { useWorkoutPdcChart, workoutHasPeakData, type WorkoutPeakBlobs } from './useWorkoutPdcChart';

interface Props {
  data: WorkoutDetail;
  userId?: number;
  chartHeight?: number;
  variant?: 'default' | 'summary';
  onWorkoutClick?: (workoutId: number) => void;
  peaks?: WorkoutPeakBlobs;
  currentLabel?: string;
  onCurrentRangeClick?: (item: Record<string, unknown>) => void;
}

/** Unified PDC chart with peak-type tabs, historical comparison, and landscape fullscreen. */
export function MobileWorkoutPdcChart({
  data,
  userId,
  chartHeight = 360,
  variant = 'default',
  onWorkoutClick,
  peaks,
  currentLabel,
  onCurrentRangeClick,
}: Props) {
  const { t } = useTranslation();
  const [paceSelected, setPaceSelected] = useState(false);
  const [selectedInterval, setSelectedInterval] = useState(300);
  const { open: fsOpen, enter: enterFs, exit: exitFs, overlayRef } = useChartLandscapeFullscreen();
  const fsChartHeight = useFullscreenChartHeight(fsOpen, overlayRef, 108);

  const {
    pdcPeakType,
    setPdcPeakType,
    compareStart,
    compareEnd,
    rangeEndDate,
    applyComparePreset,
    compareCurve,
    currentPdcCurve,
    currentPdcLabel,
    pdcLoading,
    showPdc,
  } = useWorkoutPdcChart({ workout: data, userId, peaks });

  const chartProps = useMemo(
    () => ({
      currentCurve: currentPdcCurve,
      compareCurve,
      compareStart,
      compareEnd,
      currentLabel: currentLabel ?? currentPdcLabel,
      peakType: pdcPeakType,
      selectedInterval,
      onSelectedIntervalChange: setSelectedInterval,
      paceSelected,
      onPaceSelectedChange: setPaceSelected,
      cadenceUnit: isRunningWorkout(data) ? 'spm' : 'rpm',
      powerAvailable: !isRunningWorkout(data) || runningStatsPresentation(data).power != null || Object.values((peaks ?? data.details?.peaks)?.pw_peak ?? {}).some(item => recordedNumber((item as { value?: unknown })?.value) != null),
      onPeakTypeChange: setPdcPeakType,
      onCompareStartChange: () => {},
      onCompareEndChange: () => {},
      rangeEndDate,
      onComparePresetApply: applyComparePreset,
      loading: pdcLoading,
      variant: variant as 'default' | 'summary',
      onBestWorkoutClick: (id: number) => {
        if (fsOpen) void exitFs().then(() => onWorkoutClick?.(id));
        else onWorkoutClick?.(id);
      },
      onCurrentRangeClick: onCurrentRangeClick ? (point: { interval: number }) => {
        const raw = Object.values((peaks ?? data.details?.peaks)?.[pdcPeakType] ?? {}).find(item => Number((item as { interval?: unknown }).interval) === point.interval);
        if (!raw || typeof raw !== 'object') return;
        const select = () => onCurrentRangeClick(raw as Record<string, unknown>);
        if (fsOpen) void exitFs().then(select); else select();
      } : undefined,
      compareOnlyWorkoutClick: true,
    }),
    [
      applyComparePreset,
      compareCurve,
      compareEnd,
      compareStart,
      currentPdcCurve,
      currentPdcLabel,
      currentLabel,
      pdcLoading,
      pdcPeakType,
      selectedInterval,
      paceSelected,
      rangeEndDate,
      setPdcPeakType,
      variant,
      onWorkoutClick,
      onCurrentRangeClick,
      peaks,
      data,
      data.details?.peaks,
      fsOpen,
      exitFs,
    ],
  );

  if (!workoutHasPeakData(data) && !showPdc) {
    return <p className="wp-peaks-empty">{t('workoutDetail.peaks.empty')}</p>;
  }

  if (!showPdc) return null;

  const inlineChart = (
    <PdcCurveChart
      {...chartProps}
      chartHeight={chartHeight}
      onExpand={variant === 'summary' ? enterFs : undefined}
    />
  );

  const fullscreenOverlay = fsOpen
    ? createPortal(
        <div
          ref={overlayRef}
          className="pdc-fullscreen-overlay pdc-fullscreen-overlay--pdc mobile-fullscreen-panel vo2-slide-up-fullscreen"
          role="dialog"
          aria-modal="true"
          aria-label={t('pdcChart.title')}
        >
          <header className="pdc-fullscreen-overlay__header">
            <button
              type="button"
              className="pdc-fullscreen-overlay__close pdc-chart__icon-btn"
              onClick={() => void exitFs()}
              aria-label={t('pdcChart.close')}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18" /></svg>
            </button>
            {/* <h2 className="pdc-fullscreen-overlay__title">{t('pdcChart.title')}</h2> */}
            <span className="pdc-fullscreen-overlay__spacer" aria-hidden />
          </header>
          <p className="pdc-fullscreen-overlay__hint">{t('pdcChart.rotateHint')}</p>
          <div className="pdc-fullscreen-overlay__body">
            <PdcCurveChart
              key={`pdc-fs-${fsChartHeight}`}
              {...chartProps}
              chartHeight={fsChartHeight}
              variant="summary"
              layoutMode="fullscreen"
            />
          </div>
        </div>,
        document.body,
      )
    : null;

  if (variant === 'summary') {
    return (
      <>
        <section className="pdc-nm-card pdc-nm-card--chart-only" aria-label={t('pdcChart.title')}>
          {inlineChart}
        </section>
        {fullscreenOverlay}
      </>
    );
  }

  return <div className="wp-pdc-chart">{inlineChart}</div>;
}
